/**
 * adoptionSessionAnalytics.js
 *
 * Session-derived adoption analytics added for the Admin → Adoption deep-dive
 * dashboards (concentration/overfit, value-moment activation, momentum,
 * reach-weighted Jobs-to-be-Done, ambition, session depth, prompt sentiment,
 * archetypes). These are intentionally separate from the existing
 * `adoptionAnalytics.js` functions (`activation`, `adoptionFunnel`,
 * `concentration`) which use different definitions (total-query "champion"
 * vs. a deep *session*; per-week HHI vs. a ranked top-N user list).
 *
 * Pure + deterministic so they are unit-testable and reusable by views.
 *
 * Event shape (from normalizeEvents): { user, ts(ISO), text, company?, assistant? }
 * `assistant` is optional — reply-dependent functions degrade gracefully when
 * it is absent (until the events feed surfaces assistant turns).
 */
import {
  companyOf, isoWeekStart, percentile, DEFAULT_COMPANY_ALIASES,
} from './adoptionAnalytics.js';

const DAY = 86400000;
export const DEEP_SESSION_QUERIES = 5;     // queries within a session => "deep"
export const SESSION_GAP_MIN = 30;         // session boundary (minutes)
export const CHURN_GAP_DAYS = 28;          // idle >= this at period end => churned
export const ANCHOR_COMPANY = 'EMD Serono / Merck KGaA';

// --------------------------------------------------------------------------
// shared helpers
// --------------------------------------------------------------------------
function byUserTimes(events) {
  const m = new Map();
  for (const e of events) {
    const t = Date.parse(e.ts);
    if (Number.isNaN(t)) continue;
    if (!m.has(e.user)) m.set(e.user, []);
    m.get(e.user).push(t);
  }
  for (const a of m.values()) a.sort((x, y) => x - y);
  return m;
}

function sessionsOf(times, gapMin = SESSION_GAP_MIN) {
  const gap = gapMin * 60000;
  const out = [[times[0]]];
  for (let i = 1; i < times.length; i++) {
    if (times[i] - out[out.length - 1][out[out.length - 1].length - 1] <= gap) {
      out[out.length - 1].push(times[i]);
    } else out.push([times[i]]);
  }
  return out;
}

function maxTs(events) {
  let m = -Infinity;
  for (const e of events) { const t = Date.parse(e.ts); if (t > m) m = t; }
  return m;
}

/** Set of churned users: last activity >= CHURN_GAP_DAYS before the period end. */
export function churnedSet(events, gapDays = CHURN_GAP_DAYS) {
  const last = new Map();
  for (const e of events) {
    const t = Date.parse(e.ts);
    if (!last.has(e.user) || t > last.get(e.user)) last.set(e.user, t);
  }
  const end = maxTs(events);
  const s = new Set();
  for (const [u, t] of last) if (end - t >= gapDays * DAY) s.add(u);
  return s;
}

// --------------------------------------------------------------------------
// value-moment activation funnel (deep session = "aha")
// --------------------------------------------------------------------------
export function valueMomentFunnel(events, opts = {}) {
  const deepN = opts.deepN ?? DEEP_SESSION_QUERIES;
  const gapMin = opts.gapMin ?? SESSION_GAP_MIN;
  const bu = byUserTimes(events);
  const n = bu.size;
  if (!n) return { stages: [], activationRate: 0, medianDaysToValue: null, within7Pct: 0, sustainedActivated: 0, sustainedNot: 0, churnActivated: 0, churnNot: 0 };

  const weeksOf = (times) => new Set(times.map((t) => isoWeekStart(new Date(t).toISOString())));
  const churned = churnedSet(events);
  let twoQ = 0, returned = 0, sustained = 0;
  const activated = new Set();
  const ttv = [];

  for (const [u, times] of bu) {
    if (times.length >= 2) twoQ += 1;
    const w = weeksOf(times).size;
    if (w >= 2) returned += 1;
    if (w >= 3) sustained += 1;
    let deepAt = null;
    for (const s of sessionsOf(times, gapMin)) if (s.length >= deepN) { deepAt = s[0]; break; }
    if (deepAt != null) { activated.add(u); ttv.push((deepAt - times[0]) / DAY); }
  }
  const act = activated.size;
  const notAct = [...bu.keys()].filter((u) => !activated.has(u));
  const sustainedRate = (users) => {
    if (!users.length) return 0;
    const c = users.filter((u) => weeksOf(bu.get(u)).size >= 3).length;
    return Math.round((100 * c) / users.length);
  };
  const churnRate = (users) => {
    if (!users.length) return 0;
    return Math.round((100 * users.filter((u) => churned.has(u)).length) / users.length);
  };
  const pct = (x) => Math.round((100 * x) / n);
  const within7 = ttv.filter((d) => d <= 7).length;
  return {
    stages: [
      { stage: 'Signed in (≥1 query)', count: n, pct: 100 },
      { stage: 'Ran 2+ queries', count: twoQ, pct: pct(twoQ) },
      { stage: 'Reached value moment (deep session)', count: act, pct: pct(act), activation: true },
      { stage: 'Returned 2+ weeks', count: returned, pct: pct(returned) },
      { stage: 'Sustained 3+ weeks', count: sustained, pct: pct(sustained) },
    ],
    activationRate: pct(act),
    medianDaysToValue: ttv.length ? +percentile(ttv, 50).toFixed(1) : null,
    within7Pct: act ? Math.round((100 * within7) / act) : 0,
    sustainedActivated: sustainedRate([...activated]),
    sustainedNot: sustainedRate(notAct),
    churnActivated: churnRate([...activated]),
    churnNot: churnRate(notAct),
  };
}

// --------------------------------------------------------------------------
// user-level prompt concentration + Gini + overfit guard
// --------------------------------------------------------------------------
function gini(values) {
  const v = [...values].sort((a, b) => a - b);
  const n = v.length, s = v.reduce((a, b) => a + b, 0);
  if (!n || !s) return 0;
  let cum = 0;
  for (let i = 0; i < n; i++) cum += (i + 1) * v[i];
  return +(((2 * cum) / (n * s)) - (n + 1) / n).toFixed(2);
}

export function userConcentration(events, opts = {}) {
  const aliases = opts.aliases || DEFAULT_COMPANY_ALIASES;
  const topN = opts.topN ?? 20;
  const cnt = new Map();
  for (const e of events) cnt.set(e.user, (cnt.get(e.user) || 0) + 1);
  const total = [...cnt.values()].reduce((a, b) => a + b, 0) || 1;
  const ranked = [...cnt.entries()].sort((a, b) => b[1] - a[1]);
  const cum = (k) => +(100 * ranked.slice(0, k).reduce((a, [, c]) => a + c, 0) / total).toFixed(1);
  const top = ranked.slice(0, topN).map(([u, c]) => ({
    user: u.split('@')[0], company: companyOf(u, aliases), count: c, pct: +(100 * c / total).toFixed(1),
  }));
  return {
    gini: gini([...cnt.values()]),
    total, users: cnt.size,
    top1: cum(1), top3: cum(3), top5: cum(5), top10: cum(10), top20: cum(20),
    bottomPct: +(100 - cum(20)).toFixed(1),
    topUser: ranked.length ? ranked[0][0].split('@')[0] : null,
    topCompany: ranked.length ? companyOf(ranked[0][0], aliases) : null,
    topList: top,
  };
}

// --------------------------------------------------------------------------
// momentum & expansion
// --------------------------------------------------------------------------
function daysInMonth(mk, maxDate) {
  const [y, m] = mk.split('-').map(Number);
  if (maxDate && maxDate.toISOString().slice(0, 7) === mk) return maxDate.getUTCDate();
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

export function momentum(events, opts = {}) {
  const aliases = opts.aliases || DEFAULT_COMPANY_ALIASES;
  if (!events.length) return { perDay: [], multiSeat: [], multiSeatCount: 0, totalCompanies: 0, newByMonth: [], liveBase: 0 };
  const vol = new Map(), umon = new Map(), first = new Map(), last = new Map();
  let maxDate = new Date(0);
  for (const e of events) {
    const d = new Date(e.ts); if (d > maxDate) maxDate = d;
    const mk = e.ts.slice(0, 7);
    vol.set(mk, (vol.get(mk) || 0) + 1);
    if (!umon.has(mk)) umon.set(mk, new Set());
    umon.get(mk).add(e.user);
    const t = Date.parse(e.ts);
    if (!first.has(e.user) || t < first.get(e.user)) first.set(e.user, t);
    if (!last.has(e.user) || t > last.get(e.user)) last.set(e.user, t);
  }
  const months = [...vol.keys()].sort();
  const perDay = months.map((mk) => {
    const d = daysInMonth(mk, maxDate);
    return { month: mk, prompts: vol.get(mk), days: d, perDay: +(vol.get(mk) / d).toFixed(1), activeUsers: umon.get(mk).size };
  });
  const compUsers = new Map();
  for (const u of first.keys()) {
    const c = companyOf(u, aliases);
    if (!compUsers.has(c)) compUsers.set(c, new Set());
    compUsers.get(c).add(u);
  }
  const multiSeat = [...compUsers.entries()].filter(([, s]) => s.size > 1)
    .map(([c, s]) => ({ company: c, users: s.size })).sort((a, b) => b.users - a.users);
  const fmonth = new Map();
  for (const [, t] of first) { const mk = new Date(t).toISOString().slice(0, 7); fmonth.set(mk, (fmonth.get(mk) || 0) + 1); }
  const newByMonth = months.map((mk) => ({ month: mk, new: fmonth.get(mk) || 0 }));
  const end = maxDate.getTime();
  const liveBase = [...last.values()].filter((t) => (end - t) / DAY <= CHURN_GAP_DAYS).length;
  return { perDay, multiSeat, multiSeatCount: multiSeat.length, totalCompanies: compUsers.size, newByMonth, liveBase };
}

// --------------------------------------------------------------------------
// session depth histogram
// --------------------------------------------------------------------------
export function sessionDepthHistogram(events, opts = {}) {
  const gapMin = opts.gapMin ?? SESSION_GAP_MIN;
  const lens = [];
  for (const times of byUserTimes(events).values()) for (const s of sessionsOf(times, gapMin)) lens.push(s.length);
  const n = lens.length || 1;
  const p = (f) => Math.round((100 * lens.filter(f).length) / n);
  return {
    total: lens.length,
    medianQueries: lens.length ? percentile(lens, 50) : 0,
    buckets: [
      { bucket: '1 query', pct: p((l) => l === 1) },
      { bucket: '2', pct: p((l) => l === 2) },
      { bucket: '3–4', pct: p((l) => l >= 3 && l <= 4) },
      { bucket: '5–9 (deep)', pct: p((l) => l >= 5 && l <= 9) },
      { bucket: '10+ (deep)', pct: p((l) => l >= 10) },
    ],
    deepPct: p((l) => l >= DEEP_SESSION_QUERIES),
    singlePct: p((l) => l === 1),
  };
}

// --------------------------------------------------------------------------
// reach-weighted Jobs-to-be-Done (deterministic tags) + overfit verdict
// --------------------------------------------------------------------------
const GENE_TOK = /\b[A-Z][A-Z0-9]{1,6}\b/g;
export function jobTags(text) {
  const s = text.toLowerCase(); const out = new Set();
  const geneCount = (text.match(GENE_TOK) || []).length;
  if (/\brevers/.test(s) && /(regulat|activate|inhibit|express|signature)/.test(s)) out.add('Signature reversal');
  if (/\b(smiles|sar\b|analog(ue)?s?|scaffold|substructure|chemotype|pharmacophore|tanimoto|bind(er|s)?|inhibitor of)\b/.test(s)) out.add('Compounds / SAR');
  if (/\b(cell ?line|sensitive|insensitive|resistant|biomarker|ccle|depmap|gi50|ic50|prism|synthetic lethal|vulnerab)\b/.test(s)) out.add('Cell-line / biomarker');
  if ((/\b(prioriti|nominate|rank)\b/.test(s) && /(target|gene|vulnerab)/.test(s)) || (geneCount >= 6 && /(target|gene|cancer|prioriti)/.test(s))) out.add('Gene-list target prioritization');
  if (/\b(market size|incidence|prevalence|treatment options|lines of treatment|competitor|\btaa\b|t-?cell engager|landscape)\b/.test(s)) out.add('Commercial / landscape');
  if (/\b(report|white paper|executive summary|dossier)\b/.test(s)) out.add('Report / deliverable');
  if (/\b(i have (a|an|the)? ?(list|file|data|gene|compound|cell line)|attached|the (ids|genes) (listed )?below|cannot disclose|upload|paste)\b/.test(s)) out.add('Bring-my-own-data');
  if (/\b(verify|confirm .* (reference|citation|paper)|does .* exist|reference exist|dataset|geo\b|gse\d|sequencing data|whole genome)\b/.test(s)) out.add('Find/verify dataset or reference');
  if (/\b(evidence|associat|role of|implicat|involved in|connection between)\b/.test(s)) out.add('Target-disease evidence');
  if (/\b(mechanism|pathway|mode of action|\bmoa\b|interact|binding partner)\b/.test(s)) out.add('Mechanism / pathway');
  return out;
}

export function reachWeightedJobs(events, opts = {}) {
  const aliases = opts.aliases || DEFAULT_COMPANY_ALIASES;
  const anchor = opts.anchorCompany || ANCHOR_COMPANY;
  const seen = new Set(); const users = new Map();
  for (const e of events) {
    const key = e.user + '' + e.text;
    if (seen.has(key)) continue; seen.add(key);
    for (const tg of jobTags(e.text)) {
      if (!users.has(tg)) users.set(tg, new Set());
      users.get(tg).add(e.user);
    }
  }
  const jobs = [...users.entries()].map(([job, us]) => {
    const companies = new Set([...us].map((u) => companyOf(u, aliases)));
    const nonAnchor = [...us].filter((u) => companyOf(u, aliases) !== anchor).length;
    return { job, users: us.size, companies: companies.size, nonAnchor, robust: us.size >= 5 && nonAnchor >= 3 && companies.size >= 3 };
  }).sort((a, b) => b.users - a.users);
  return jobs;
}

// --------------------------------------------------------------------------
// ambition score (per last turn) — 0..10
// --------------------------------------------------------------------------
const MODALITY = /\b(bispecific|trispecific|multispecific|bi-?functional|t-?cell engager|nk (cell )?engager|\btce\b|\bcar-?t\b|protac|molecular glue|degrader|\badc\b|antibody-?drug|allosteric|covalent inhibitor)\b/i;
const GENERATIVE = /\b(design|generate|nominate (novel|new)|discover|invent|de novo|propose (new|novel)|create (a )?(new|novel)|build (a|an in))\b/i;
const MULTIOBJ = /\b(combination|combo|dual|multi-?target|bispecific|selectivit|without (affecting|hitting)|but not|simultaneous|trade-?off|while (avoiding|sparing))\b/i;
const CAMPAIGN = /\b(campaign|pipeline|end-?to-?end|comprehensive|full report|strategy|workflow|prioriti[sz]e|cascade|program|protocol)\b/i;
const PREDICT = /\b(predict|likelihood of success|probability|estimate the|mechanistic hypothes|causal|forecast|model the|simulate|in silico)\b/i;
const OMICS = /\b(gene|expression|transcriptom|rna-?seq|eqtl|differential|signature|pathway|knockout|crispr|cell line|depmap|perturb)\b/i;
const CHEM = /\b(smiles|compound|molecule|inhibitor|agonist|antagonist|chemic|analog|scaffold|substructure|chemotype)\b/i;
const FOLLOWUP = /^\s*(try again|please try again|do the same|continue|next|go ahead|yes|option \d|give me (a )?report|the same|rerun)\b/i;
const LOOKUP = /^\s*(what is the (incidence|prevalence|role|mechanism|function)|what is |mechanism of action of|role of [A-Z0-9]+\b|define |which cancer)/i;

export function ambitionScore(text) {
  const s = text || ''; let p = 0;
  if (MODALITY.test(s)) p += 3;
  if (GENERATIVE.test(s)) p += 2;
  if (MULTIOBJ.test(s)) p += 2;
  if (CAMPAIGN.test(s)) p += 2;
  if (PREDICT.test(s)) p += 2;
  if (OMICS.test(s) && CHEM.test(s)) p += 2;
  p += Math.min(2, Math.floor(s.length / 180));
  if (FOLLOWUP.test(s) && s.length < 60) p -= 2;
  if (LOOKUP.test(s) && s.length < 90) p -= 2;
  return Math.max(0, Math.min(10, p));
}

export function ambitionRanking(events, opts = {}) {
  const aliases = opts.aliases || DEFAULT_COMPANY_ALIASES;
  const churned = churnedSet(events);
  const lastByUser = new Map();
  for (const e of events) {
    const t = Date.parse(e.ts);
    if (!lastByUser.has(e.user) || t > lastByUser.get(e.user).t) lastByUser.set(e.user, { t, text: e.text });
  }
  return [...lastByUser.entries()].map(([u, { text }]) => ({
    user: u.split('@')[0], company: companyOf(u, aliases),
    churned: churned.has(u), last: ambitionScore(text), lastTurn: text,
  })).sort((a, b) => b.last - a.last);
}

// --------------------------------------------------------------------------
// prompt sentiment (user text) — positive vs frustration, churned vs active
// --------------------------------------------------------------------------
const POS = /\b(thanks?|thank you|great|perfect|excellent|very helpful|love this|exactly what|brilliant|amazing|fantastic|super helpful)\b/i;
const FRUST = /\b(wrong|incorrect|mistake|error|doesn'?t|didn'?t|can'?t|cannot|no results?|nothing|missing|fail|not (working|helpful|right|able)|why (didn'?t|can'?t)|still|again|but you|not find|couldn'?t find)\b/i;

export function promptSentiment(events) {
  const churned = churnedSet(events);
  const rate = (pred, set) => {
    const ps = events.filter((e) => set.has(e.user));
    return ps.length ? Math.round((100 * ps.filter((e) => pred.test(e.text)).length) / ps.length) : 0;
  };
  const all = new Set(events.map((e) => e.user));
  const active = new Set([...all].filter((u) => !churned.has(u)));
  return {
    posChurnedPct: rate(POS, churned), posActivePct: rate(POS, active),
    frustChurnedPct: rate(FRUST, churned), frustActivePct: rate(FRUST, active),
  };
}

// --------------------------------------------------------------------------
// archetypes (rule-based segmentation)
// --------------------------------------------------------------------------
export function archetypes(events, opts = {}) {
  const deepN = opts.deepN ?? DEEP_SESSION_QUERIES;
  const bu = byUserTimes(events);
  const weeksOf = (times) => new Set(times.map((t) => isoWeekStart(new Date(t).toISOString()))).size;
  let power = 0, intense = 0, light = 0;
  for (const times of bu.values()) {
    const deep = sessionsOf(times, opts.gapMin ?? SESSION_GAP_MIN).filter((s) => s.length >= deepN).length;
    const w = weeksOf(times);
    if (deep >= 2 && w >= 3) power += 1;
    else if (deep >= 1) intense += 1;
    else light += 1;
  }
  const n = bu.size || 1;
  return [
    { archetype: 'Power core', users: power, pct: Math.round((100 * power) / n) },
    { archetype: 'Intense-but-brief', users: intense, pct: Math.round((100 * intense) / n) },
    { archetype: 'Light triallers', users: light, pct: Math.round((100 * light) / n) },
  ];
}
